/**
 * AccountPage (V2 Settings) — ported to match the V1 Settings structure and
 * visuals 1:1: profile header + plan badge, free-only upgrade banner, then the
 * ACCOUNT / SETTINGS / SOCIAL / ABOUT / HELP / MORE sections.
 *
 * Manage Subscription opens the self-service cancellation funnel
 * (CancelSubscriptionFlow): reason → lower-cost plan → are-you-sure → coupon →
 * final feedback → Stripe. It replaced the old in-page funnel that ended in
 * "email support@vivideo.ai and we'll cancel it for you".
 *
 * V2 adaptations: routes to /subscription (V1 parity slug), Help + Report-a-Bug
 * open the shared SupportModal, and feedback is via toasts.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/v2/utils';
import { useUserStore } from '@/store/userStore';
import { useSettingsStore, LANGUAGES, type Language } from '@/store/settingsStore';
import { apiService } from '@/api/apiService';
import { signOut, getIdToken } from '@/auth';
import { canManageSubscription, isPaidPlan } from '@/utils/planHelpers';
import { SupportModal } from '@/v2/components/SupportModal';
import { CancelSubscriptionFlow } from '@/v2/components/CancelSubscriptionFlow';
import { toast } from '@/v2/toast';
import { trackLanguageChanged } from '@/services/analyticsService';
import {
  Crown, LogOut, Globe, Share2, HelpCircle, FileText, Shield, ChevronDown,
  Bug, Instagram, Linkedin, Youtube, Facebook, Clock, Briefcase, Star, Info, Package,
  Settings, AlertTriangle, Loader2, KeyRound, X,
} from 'lucide-react';

const APP_VERSION = '2.0.0';

// A settings list row (icon + label + trailing element). Matches V1 exactly.
function Row({ icon: Icon, iconWrap, label, labelClass, trailing, onClick, href }: {
  icon: typeof Clock;
  iconWrap?: React.ReactNode;       // custom icon container (social brand chips)
  label: string;
  labelClass?: string;
  trailing?: React.ReactNode;       // defaults to a chevron
  onClick?: () => void;
  href?: string;
}) {
  const inner = (
    <>
      <div className="flex items-center gap-4">
        {iconWrap ?? <Icon className="w-6 h-6 text-text-primary" />}
        <span className={cn('text-base text-text-primary', labelClass)}>{label}</span>
      </div>
      {trailing ?? <ChevronDown className="w-5 h-5 text-text-tertiary -rotate-90" />}
    </>
  );
  const cls = 'w-full flex items-center justify-between p-4 rounded-xl bg-surface-light hover:bg-surface-light/70 transition-colors';
  if (href) {
    return <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>{inner}</a>;
  }
  return <button onClick={onClick} className={cls}>{inner}</button>;
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-surface rounded-2xl border border-glass-border p-6 mb-6">
      <h3 className="text-sm font-semibold text-text-secondary mb-4">{title.toUpperCase()}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

export function AccountPage() {
  const { t } = useTranslation('v2');
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const userId = useUserStore((s) => s.userId);
  const clearUser = useUserStore((s) => s.clearUser);
  const language = useSettingsStore((s) => s.language);
  const setLanguage = useSettingsStore((s) => s.setLanguage);

  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Delete-account is a plain type-DELETE-to-confirm dialog — NO retention
  // funnel (owner directive). Cancelling a SUBSCRIPTION is a separate flow.
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // Email-OTP sessions carry no Firebase token, and delete-user (strict auth)
  // requires a fresh emailed code as their ownership proof.
  const [deleteNeedsOtp, setDeleteNeedsOtp] = useState(false);
  const [deleteOtp, setDeleteOtp] = useState('');
  const [deleteOtpSending, setDeleteOtpSending] = useState(false);
  const [showCancelFlow, setShowCancelFlow] = useState(false);

  const displayName = user?.displayName || user?.name || t('account.defaultUserName');
  const email = user?.email || '';
  const avatarUrl = user?.profilePictureUrl;
  const isPro = isPaidPlan(user?.userPlan);
  const credits = user?.remainingCredit ?? 0;
  const planName = user?.userPlan === 'max' ? 'MAX' : isPro ? 'PRO' : t('account.planFree');
  const currentLanguage = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];

  const handleSignOut = async () => {
    try { await signOut(); } finally {
      // Clear the local session explicitly — email-OTP sessions aren't backed by
      // Firebase, so relying on the auth listener to clear them would leave them
      // logged in (AuthContext now restores email sessions on a null Firebase user).
      clearUser();
      localStorage.removeItem('vivideo_user_id');
      localStorage.removeItem('vivideo_email_auth');
      navigate('/login');
    }
  };

  const handleDeleteAccount = async () => {
    if (!userId) return;
    setDeleteLoading(true);
    try {
      await apiService.deleteUser(userId, deleteNeedsOtp ? deleteOtp.trim() : undefined);
      await handleSignOut();
    } catch (err) {
      setDeleteLoading(false);
      const status = (err as { response?: { status?: number } })?.response?.status;
      toast.error(deleteNeedsOtp && status === 401 ? t('account.deleteOtpInvalid') : t('account.deleteFailed'));
    }
  };

  const sendDeleteOtp = async () => {
    if (!user?.email) return;
    setDeleteOtpSending(true);
    try {
      await apiService.sendEmailOtp(user.email);
      toast.info(t('account.deleteOtpSent'));
    } catch {
      toast.error(t('account.deleteOtpSendFailed'));
    } finally {
      setDeleteOtpSending(false);
    }
  };

  // Delete = the type-DELETE-to-confirm dialog directly (NOT gated on isPro —
  // anyone can delete). No retention steps (owner directive). Sessions without
  // a Firebase token (email-OTP logins) additionally verify a fresh emailed
  // code — the backend requires it as their ownership proof and burns it
  // single-use inside the delete call itself.
  const openDeleteFunnel = async () => {
    setDeleteConfirmText(''); setDeleteOtp(''); setShowDeleteModal(true);
    const needsOtp = !(await getIdToken());
    setDeleteNeedsOtp(needsOtp);
    if (needsOtp) void sendDeleteOtp();
  };

  const handleShare = async () => {
    const shareData = {
      title: t('account.shareTitle'),
      text: t('account.shareText'),
      url: 'https://vivideo.ai',
    };
    try {
      if (navigator.share) { await navigator.share(shareData); }
      else { await navigator.clipboard.writeText(shareData.url); toast.success(t('account.inviteLinkCopied')); }
    } catch { /* user dismissed the share sheet */ }
  };

  // Manage Subscription = the self-service cancellation funnel.
  const openManageModal = () => {
    if (!canManageSubscription(user?.userPlan)) { toast.info(t('account.noActiveSubscription')); return; }
    setShowCancelFlow(true);
  };

  const btnPrimary = 'w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white text-sm font-semibold transition-all disabled:opacity-60';

  // Delete-account dialog (type DELETE to confirm). The subscription-cancel
  // funnel lives in <CancelSubscriptionFlow /> and is opened from the
  // Manage Subscription row.
  const renderDeleteModal = () => {
    if (!showDeleteModal) return null;
    const closeDeleteModal = () => setShowDeleteModal(false);
    return createPortal(
      <div className="fixed inset-0 z-[110] flex items-center justify-center animate-fade-in">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closeDeleteModal} />
        <div className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto mx-4 rounded-2xl bg-surface border border-glass-border shadow-2xl">
          <button onClick={closeDeleteModal} className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-surface-light transition-colors z-20">
            <X className="w-5 h-5 text-text-tertiary" />
          </button>

          <div className="p-6 md:p-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4"><AlertTriangle className="w-8 h-8 text-red-500" /></div>
                <h2 className="text-xl font-bold text-text-primary mb-1">{t('account.deletePermanentlyTitle')}</h2>
                <p className="text-sm text-text-secondary">{t('account.deletePermanentlyDesc')}</p>
              </div>
              <div className="mb-5 flex items-start gap-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 px-3.5 py-3">
                <Info className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-200/90 leading-relaxed">{t('account.deleteSubscriptionNotice')}</p>
              </div>
              <label className="block text-sm text-text-secondary mb-2">{t('account.deleteConfirmPrompt', { word: t('account.deleteConfirmWord') })}</label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={t('account.deleteConfirmWord')}
                autoComplete="off"
                autoCapitalize="characters"
                className="w-full mb-5 px-4 py-3 rounded-xl bg-surface-light border border-glass-border text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-red-500/50 transition-colors"
              />
              {deleteNeedsOtp && (
                <>
                  <label className="block text-sm text-text-secondary mb-2 flex items-center gap-1.5">
                    <KeyRound className="w-4 h-4 text-text-tertiary" />{t('account.deleteOtpPrompt')}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={deleteOtp}
                    onChange={(e) => setDeleteOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder="••••••"
                    className="w-full mb-2 px-4 py-3 rounded-xl bg-surface-light border border-glass-border text-text-primary tracking-[0.3em] placeholder:text-text-tertiary focus:outline-none focus:border-red-500/50 transition-colors"
                  />
                  <button
                    onClick={() => void sendDeleteOtp()}
                    disabled={deleteOtpSending}
                    className="mb-5 text-xs text-text-tertiary hover:text-text-secondary disabled:opacity-50 transition-colors"
                  >{t('account.deleteOtpResend')}</button>
                </>
              )}
              <button
                onClick={() => void handleDeleteAccount()}
                disabled={deleteLoading || deleteConfirmText.trim().toUpperCase() !== t('account.deleteConfirmWord').toUpperCase() || (deleteNeedsOtp && deleteOtp.trim().length !== 6)}
                className={cn(btnPrimary, 'mb-3 bg-red-500 hover:bg-red-600')}
              >{deleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />} {t('account.deleteMyAccount')}</button>
            <button onClick={closeDeleteModal} className="w-full py-2.5 text-sm text-text-tertiary hover:text-text-secondary transition-colors">{t('account.keepMyAccount')}</button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const brandChip = (bg: string, node: React.ReactNode) => (
    <div className={cn('w-6 h-6 rounded-md flex items-center justify-center', bg)}>{node}</div>
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto animate-fade-in pb-12 pt-8">
        <h1 className="text-2xl font-bold text-text-primary mb-6">{t('account.settings')}</h1>

        {/* Profile */}
        <div className="flex flex-col items-center mb-8">
          <div className="relative mb-4">
            {avatarUrl ? (
              <img src={avatarUrl} alt={displayName} className="w-20 h-20 rounded-full object-cover ring-2 ring-primary/30" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-amber-700 flex items-center justify-center ring-2 ring-primary/30">
                <span className="text-3xl font-bold text-white">{displayName.charAt(0).toUpperCase()}</span>
              </div>
            )}
            <div className={cn(
              'absolute -bottom-1.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full text-xs font-semibold',
              user?.userPlan === 'max' ? 'bg-gradient-to-r from-primary to-accent text-white'
                : isPro ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                : 'bg-surface-light border border-glass-border text-text-primary'
            )}>{planName}</div>
          </div>
          <h2 className="text-lg font-semibold text-text-primary">{displayName}</h2>
          <p className="text-sm text-text-secondary">{email}</p>
        </div>

        {/* Upgrade banner — free users only */}
        {!isPro && (
          <button onClick={() => navigate('/subscription')} className="w-full mb-6 p-4 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center gap-3 hover:opacity-90 transition-opacity">
            <div className="p-2.5 bg-white/20 rounded-xl"><Crown className="w-6 h-6 text-white animate-pulse" /></div>
            <div className="flex-1 text-left">
              <p className="text-sm font-bold text-white">{t('account.upgradeBannerTitle')}</p>
              <p className="text-xs text-white/70">{t('account.upgradeBannerDesc')}</p>
            </div>
            <ChevronDown className="w-4 h-4 text-white -rotate-90" />
          </button>
        )}

        {/* ACCOUNT */}
        <SectionCard title={t('account.sectionAccount')}>
          <Row icon={Clock} label={t('account.credits')} onClick={() => navigate('/subscription')}
            trailing={<span className="px-3 py-1 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold text-sm shadow-lg shadow-purple-500/40">{credits}</span>} />
          <Row icon={Briefcase} label={t('account.subscription')} onClick={() => navigate('/subscription')}
            trailing={<span className={cn('text-base font-semibold', isPro ? 'text-purple-400' : 'text-text-primary')}>{planName}</span>} />
          <Row icon={Star} label={t('account.ourPlans')} onClick={() => navigate('/subscription')} />
        </SectionCard>

        {/* SETTINGS */}
        <SectionCard title={t('account.sectionSettings')}>
          <div className="relative">
            <Row icon={Globe} label={t('account.language')} onClick={() => setShowLanguageDropdown((v) => !v)}
              trailing={<div className="flex items-center gap-2"><span className="text-lg">{currentLanguage.flag}</span><ChevronDown className={cn('w-5 h-5 text-text-tertiary transition-transform', showLanguageDropdown && 'rotate-180')} /></div>} />
            {showLanguageDropdown && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowLanguageDropdown(false)} />
                <div className="absolute right-0 top-full mt-2 w-48 py-2 bg-surface border border-glass-border rounded-xl shadow-xl z-50 max-h-64 overflow-y-auto">
                  {LANGUAGES.map((lang) => (
                    <button key={lang.code} onClick={() => { setLanguage(lang.code as Language); trackLanguageChanged(lang.code); setShowLanguageDropdown(false); }}
                      className={cn('w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-surface-light transition-colors', language === lang.code && 'bg-primary/10 text-primary')}>
                      <span className="text-lg">{lang.flag}</span><span className="text-sm">{lang.name}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </SectionCard>

        {/* SOCIAL */}
        <SectionCard title={t('account.sectionSocial')}>
          <Row icon={Globe} label={t('account.visitWebsite')} href="https://vivideo.ai" />
          <Row label={t('account.followInstagram')} href="https://www.instagram.com/vivideo.ai/" icon={Instagram}
            iconWrap={brandChip('bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500', <Instagram className="w-3.5 h-3.5 text-white" />)} />
          <Row label={t('account.followFacebook')} href="https://www.facebook.com/profile.php?id=61585145055158" icon={Facebook}
            iconWrap={brandChip('bg-[#1877F2]', <Facebook className="w-3.5 h-3.5 text-white" />)} />
          <Row label={t('account.followLinkedIn')} href="https://www.linkedin.com/company/ai-video-generator-vivideo" icon={Linkedin}
            iconWrap={brandChip('bg-[#0077B5]', <Linkedin className="w-3.5 h-3.5 text-white" />)} />
          <Row label={t('account.followX')} href="https://x.com/vivideoai" icon={Globe}
            iconWrap={brandChip('bg-text-primary', <span className="text-background font-bold text-xs">𝕏</span>)} />
          <Row label={t('account.followYouTube')} href="https://www.youtube.com/@VivideoAI" icon={Youtube}
            iconWrap={brandChip('bg-[#FF0000]', <Youtube className="w-3.5 h-3.5 text-white" />)} />
        </SectionCard>

        {/* ABOUT */}
        <SectionCard title={t('account.sectionAbout')}>
          <Row icon={Share2} label={t('account.inviteFriends')} onClick={() => void handleShare()} />
          <Row icon={Info} label={t('account.aboutUs')} href="https://vivideo.ai" />
          <Row icon={Settings} label={t('account.manageSubscription')} onClick={openManageModal} />
          <Row icon={Shield} label={t('account.privacyPolicy')} href="https://vivideo.ai/privacy-policy" />
          <Row icon={FileText} label={t('account.termsOfService')} href="https://vivideo.ai/terms-of-service" />
        </SectionCard>

        {/* HELP */}
        <SectionCard title={t('account.sectionHelp')}>
          <Row icon={Bug} label={t('account.reportABug')} onClick={() => setSupportOpen(true)} />
          <Row icon={HelpCircle} label={t('account.help')} onClick={() => setSupportOpen(true)} />
        </SectionCard>

        {/* MORE */}
        <div className="bg-surface rounded-2xl border border-glass-border mb-6 overflow-hidden">
          <div className="w-full flex items-center justify-between p-6">
            <h3 className="text-sm font-semibold text-text-secondary">{t('account.sectionMore')}</h3>
          </div>
          <div className="px-6 pb-6 space-y-2">
            {/* PUBLIC-API keys — manage programmatic access (developers.vivideo.ai). */}
            <Row icon={KeyRound} label={t('account.apiKeys')} onClick={() => navigate('/account/api-keys')} />
            <div className="w-full flex items-center justify-between p-4 rounded-xl bg-surface-light">
              <div className="flex items-center gap-4"><Package className="w-6 h-6 text-text-primary" /><span className="text-base text-text-primary">{t('account.version')}</span></div>
              <span className="text-sm text-text-secondary">{APP_VERSION}</span>
            </div>
            <button onClick={() => void openDeleteFunnel()} className="w-full flex items-center justify-between p-4 rounded-xl bg-surface-light hover:bg-red-500/10 transition-colors">
              <div className="flex items-center gap-4"><AlertTriangle className="w-6 h-6 text-red-500" /><span className="text-base text-red-500">{t('account.deleteAccount')}</span></div>
              <ChevronDown className="w-5 h-5 text-text-tertiary -rotate-90" />
            </button>
            <Row icon={LogOut} label={t('account.signOut')} onClick={() => void handleSignOut()} />
          </div>
        </div>
      </div>

      {renderDeleteModal()}
      {showCancelFlow && <CancelSubscriptionFlow onClose={() => setShowCancelFlow(false)} />}
      {supportOpen && <SupportModal onClose={() => setSupportOpen(false)} />}
    </div>
  );
}
